import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type AssistantSliceState = {
    // Supported Contour+ models for adv op assistant
    roiModels: string[] | undefined;
    areRoiModelsLoading: boolean;
    roiModelsError: string | null;

    // Regex generation
    isRegexGenerating: boolean;
    regexError: string | null;
    regexResult: string | null;
    regexRequestId: string | null;

    // ROI advanced operation generation
    isRoiOpGenerating: boolean;
    roiOpError: string | null;
    roiOpResult: { operation: string; validationStatus: string } | null;
    roiOpThreadId: string | null;
    roiOpRequestId: string | null;

    // ROI advanced operation validation
    isRoiValidating: boolean;
    roiValidateError: string | null;
    roiValidateResults: Array<{ operation: string; valid: boolean; error: string }> | null;
};

export const initialState: AssistantSliceState = {
    roiModels: undefined,
    areRoiModelsLoading: false,
    roiModelsError: null,

    isRegexGenerating: false,
    regexError: null,
    regexResult: null,
    regexRequestId: null,

    isRoiOpGenerating: false,
    roiOpError: null,
    roiOpResult: null,
    roiOpThreadId: null,
    roiOpRequestId: null,

    isRoiValidating: false,
    roiValidateError: null,
    roiValidateResults: null,
};

const assistantSlice = createSlice({
    name: 'assistant',
    initialState,
    reducers: {
        // supported Contour+ models
        fetchRoiModelsStarted(state) {
            state.areRoiModelsLoading = true;
            state.roiModelsError = null;
        },
        fetchRoiModelsSucceeded(state, action: PayloadAction<string[]>) {
            state.roiModels = action.payload;
            state.areRoiModelsLoading = false;
            state.roiModelsError = null;
        },
        fetchRoiModelsFailed(state, action: PayloadAction<string>) {
            state.areRoiModelsLoading = false;
            state.roiModelsError = action.payload;
        },

        // Regex generation
        generateRegexStarted(state, action: PayloadAction<{ description: string; matchExamples: string; nonMatchExamples: string; requestId: string }>) {
            state.isRegexGenerating = true;
            state.regexError = null;
            state.regexResult = null;
            state.regexRequestId = action.payload.requestId;
        },
        generateRegexSucceeded(state, action: PayloadAction<{ regex: string; requestId: string }>) {
            if (action.payload.requestId !== state.regexRequestId) { return; }
            state.isRegexGenerating = false;
            state.regexResult = action.payload.regex;
        },
        generateRegexFailed(state, action: PayloadAction<{ error: string; requestId: string }>) {
            if (action.payload.requestId !== state.regexRequestId) { return; }
            state.isRegexGenerating = false;
            state.regexError = action.payload.error;
        },

        // ROI advanced operation generation
        generateRoiOperationStarted(state, action: PayloadAction<{ query: string; model: string; userThreadId: string; requestId: string }>) {
            state.isRoiOpGenerating = true;
            state.roiOpError = null;
            state.roiOpResult = null;
            state.roiOpRequestId = action.payload.requestId;
        },
        generateRoiOperationSucceeded(state, action: PayloadAction<{ operation: string; validationStatus: string; userThreadId: string; requestId: string }>) {
            if (action.payload.requestId !== state.roiOpRequestId) { return; }
            state.isRoiOpGenerating = false;
            state.roiOpResult = { operation: action.payload.operation, validationStatus: action.payload.validationStatus };
            state.roiOpThreadId = action.payload.userThreadId;
        },
        generateRoiOperationFailed(state, action: PayloadAction<{ error: string; requestId: string }>) {
            if (action.payload.requestId !== state.roiOpRequestId) { return; }
            state.isRoiOpGenerating = false;
            state.roiOpError = action.payload.error;
        },

        // ROI advanced operation validation
        validateRoiOperationsStarted(state, _action: PayloadAction<{ operations: string }>) {
            state.isRoiValidating = true;
            state.roiValidateError = null;
            state.roiValidateResults = null;
        },
        validateRoiOperationsSucceeded(state, action: PayloadAction<Array<{ operation: string; valid: boolean; error: string }>>) {
            state.isRoiValidating = false;
            state.roiValidateResults = action.payload;
        },
        validateRoiOperationsFailed(state, action: PayloadAction<string>) {
            state.isRoiValidating = false;
            state.roiValidateError = action.payload;
        },

        // Resets
        resetRegexAssistant(state) {
            state.isRegexGenerating = false;
            state.regexError = null;
            state.regexResult = null;
            state.regexRequestId = null;
        },
        resetRoiOpAssistant(state) {
            state.isRoiOpGenerating = false;
            state.roiOpError = null;
            state.roiOpResult = null;
            state.roiOpThreadId = null;
            state.roiOpRequestId = null;
        },
        resetAssistantState() {
            return initialState;
        },
    },
    selectors: {
        selectRoiModels: (state) => state.roiModels,
        selectAreRoiModelsLoading: (state) => state.areRoiModelsLoading,
        selectRoiModelsError: (state) => state.roiModelsError,

        selectIsRegexGenerating: (state) => state.isRegexGenerating,
        selectRegexError: (state) => state.regexError,
        selectRegexResult: (state) => state.regexResult,

        selectIsRoiOpGenerating: (state) => state.isRoiOpGenerating,
        selectRoiOpError: (state) => state.roiOpError,
        selectRoiOpResult: (state) => state.roiOpResult,
        selectRoiOpThreadId: (state) => state.roiOpThreadId,

        selectIsRoiValidating: (state) => state.isRoiValidating,
        selectRoiValidateError: (state) => state.roiValidateError,
        selectRoiValidateResults: (state) => state.roiValidateResults,
    },
});

export const {
    fetchRoiModelsStarted, fetchRoiModelsSucceeded, fetchRoiModelsFailed,
    generateRegexStarted, generateRegexSucceeded, generateRegexFailed,
    generateRoiOperationStarted, generateRoiOperationSucceeded, generateRoiOperationFailed,
    validateRoiOperationsStarted, validateRoiOperationsSucceeded, validateRoiOperationsFailed,
    resetRegexAssistant, resetRoiOpAssistant, resetAssistantState,
} = assistantSlice.actions;

export const { getInitialState, selectors: assistantSelectors } = assistantSlice;

export default assistantSlice.reducer;
